import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  getLastSyncDate,
  syncCSVClientData,
  syncData,
  exportAllTestIndicesFromElasticsearch,
  updateExistingWorkshopsInES,
} from '../../../services/syncData.service';
import { toast } from 'react-toastify';
import { LoadingButton } from '../../../components/LoadingButton/LoadingButton';
import { PersonOutline, Sync } from '@mui/icons-material';
import dayjs from 'dayjs';
import { DashboardSmallCard } from '../../../components/DashboardSmallCard/DashboardSmallCard';
import { useAppSelector } from '../../../store/store';
import { UserModel } from '../../../store/user/userModel';
import { UserRolesEnum } from '../../../utils/rolesEnum';
import LegendToggleIcon from '@mui/icons-material/LegendToggle';

const Setting = () => {
  const navigate = useNavigate();
  const user: UserModel = useAppSelector((state: any) => state.user.user);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [exportLoading, setExportLoading] = React.useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = React.useState<boolean>(false);
  const [isFileUploaded, setIsFileUploaded] = React.useState<boolean>(false);
  const [lastSyncDate, setLastSyncDate] = React.useState<any>();

  React.useEffect(() => {
    (async () => {
      const result = await getLastSyncDate();
      setLastSyncDate(result);
    })();
  }, [isFileUploaded]);

  const handleClick = async () => {
    try {
      setLoading(true);
      const result: any = await syncData();
      if (result && result.success) {
        toast.success(result.message);
      } else {
        toast.error('Data Syncing Failed!');
      }
      await syncCSVClientData();
      setLoading(false);
      setIsFileUploaded(true);
    } catch (error: any) {
      console.log(error.message);
      setLoading(false);
    }
  };

  // Temporary changes for exporting clients from Elasticsearch
  const handleExportAllTestIndicesFromElasticsearch = async () => {
    try {
      setExportLoading(true);
      await exportAllTestIndicesFromElasticsearch('json');
      setExportLoading(false);
    } catch (error: any) {
      console.log(error.message);
      setExportLoading(false);
    }
  };

  const handleUpdateExistingWorkshopsDataInES = async () => {
    try {
      setUpdateLoading(true);
      await updateExistingWorkshopsInES();
      toast.success('Workshops data updated successfully');
    } catch (error: any) {
      console.error(error.message);
      toast.error('Failed to update workshops data');
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} mt={2} mb={2}>
          <Typography variant='h5'>Settings</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={1} direction='column' justifyContent='center' alignItems='center'>
        <Grid item lg={4} xs={12}>
          <DashboardSmallCard
            label='User Management'
            desc='Add or manage your staff members'
            disableRipple={true}
            avatar={<PersonOutline />}
            action={
              <Box mt={2} px={2}>
                <Button onClick={() => navigate('/admin/user')}>
                  <Typography variant='button'>Manage</Typography>
                </Button>
              </Box>
            }
          />
        </Grid>
        <Grid item lg={4} xs={12} display={user.role === UserRolesEnum.SUPER_ADMIN ? 'block' : 'none'}>
          <DashboardSmallCard
            label='Monitoring System'
            desc='Track client activities'
            disableRipple={true}
            avatar={<LegendToggleIcon />}
            action={
              <Box mt={2} px={2}>
                <Button onClick={() => navigate('/admin/monitoring')}>
                  <Typography variant='button'>Monitor</Typography>
                </Button>
              </Box>
            }
          />
        </Grid>
        <Grid item lg={4} xs={12} display={process.env.REACT_APP_PROJECT_ID !== 'nyatlas-test' ? 'block' : 'none'}>
          <DashboardSmallCard
            label='Sync Data'
            desc='Sync client files with sftp server'
            disableRipple={true}
            avatar={<Sync />}
            action={
              <Box mt={2} px={4.5}>
                <LoadingButton variant='text' onClick={handleClick} loading={loading} label='Sync' />
              </Box>
            }
          />

          {/* Last Synced Date */}
          <Box>
            <Typography textAlign={'right'} fontSize={12} fontWeight={'500'} fontStyle={'italic'}>
              Last synced on: As of{' '}
              {lastSyncDate ? dayjs(lastSyncDate?.lastSyncAt.toDate()).format('M/D/YYYY h:mma EST') : '---'}
            </Typography>
          </Box>
        </Grid>
        {/* Temporary changes for exporting clients from Elasticsearch */}
        <Grid item lg={4} xs={12} display={process.env.REACT_APP_PROJECT_ID === 'nyatlas-test' ? 'block' : 'none'}>
          <DashboardSmallCard
            label='Export All Test Indices from Elasticsearch'
            desc='Download all test indices as JSON file'
            disableRipple={true}
            avatar={<Sync />}
            action={
              <Box mt={2} px={4.5}>
                <LoadingButton
                  variant='text'
                  onClick={handleExportAllTestIndicesFromElasticsearch}
                  loading={exportLoading}
                  label='Export'
                />
              </Box>
            }
          />
        </Grid>
        {/* <LoadingButton onClick={firstHandleClick} loading={firstLoading} label='remove empty created date' /> */}
        {/* <Box>
          <LoadingButton onClick={secondHandleClick} loading={secondLoading} label='Add Created Date' />
        </Box> */}
        <Grid item lg={4} xs={12} display={process.env.REACT_APP_PROJECT_ID === 'nyatlas-test' ? 'block' : 'none'}>
          <DashboardSmallCard
            label='Update Existing Workshops Data in Elasticsearch'
            desc='Update Existing Workshops Data in Elasticsearch'
            disableRipple={true}
            avatar={<Sync />}
            action={
              <Box mt={2} px={4.5}>
                <LoadingButton
                  variant='text'
                  onClick={handleUpdateExistingWorkshopsDataInES}
                  loading={updateLoading}
                  label='Update'
                />
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Setting;
